import React, { FC, useState, useEffect, useRef } from 'react'
import classNamesBind from 'classnames/bind'
import * as ls from 'local-storage'
import Router from 'next/router'
import FullStory, { FullStoryAPI } from 'react-fullstory'
import {
  CRYSTAL_AUTH_TOKEN,
  CRYSTAL_SESSION_TOKEN,
} from '@dashboard/lib/constants'
import OrganizationInviteManager from '@dashboard/components/OrganizationInviteManager'

import { toArchetype } from '@dashboard/lib/personality'
import { FullAuthUser, SegmentTypes, USER_ACTIONS } from '@dashboard/lib/types'
import { deleteCookie, getQueryParam } from '@dashboard/lib/browser'
import { useAppStateSelector } from '@dashboard/hooks/useAppStateSelector'
import { useLoadAuthUser } from '@dashboard/hooks/user/useLoadMe'
import { updateUserSettings } from '@dashboard/hooks/user/updateUserSettings'
import {
  currentAccess,
  currentPlans,
  customTermsOfServiceNotAccepted,
  isFreeUser,
  isHiringUser,
  isSalesUser,
  isLeadershipUser,
  isOnFreeTeam,
  isEnterpriseUser,
} from '@dashboard/lib/user'
import { rsGroup, rsIdentify } from '@dashboard/lib/analytics'
import {
  currentSegment,
  hiringAccessRevoked,
  isAdmin,
  localAuthToken,
} from '@dashboard/lib/user'
import ProductSurveyRecruitment from '@dashboard/components/ProductSurveyRecruitment'
import moment from 'moment'
import { useUserCriteria } from '@dashboard/hooks/user/useUserCriteria'
import { UserCriteriaResponse } from '@dashboard/store/actions/user/criteria'
import UpgradeBanner from './components/UpgradeBanner'
import Toast from '@dashboard/components/Toast'
import { ParsedUrlQuery } from 'querystring'
import { AUTH_QUERY_PARAMS_STORAGE_KEY } from '@dashboard/components/Registration/component'
import LoadingBox from '@dashboard/components/LoadingBox'
import NotificationBar from '@dashboard/components/Navigation/components/NotificationBar'

import css from './styles.module.scss'
import useAuth from '@crystal-eyes/hooks/useAuth'
import MainContent from '@dashboard/components_v2/global/MainContent'
import useSideNavFlag from '@dashboard/hooks/useSideNavFlag'
const classNames = classNamesBind.bind(css)
const SESSION_START_TIME_KEY = 'session_start_time'

type OwnProps = {
  background?: string
  hideFooter?: boolean
  pageName: string
  simpleNav?: boolean
  hideAssessmentNotification?: boolean
  hideNavOnPrint?: boolean
  segment?: SegmentTypes
  isPublicPage?: boolean
  authUser?: FullAuthUser | null
  childRenderer: (authUser: FullAuthUser) => React.ReactNode
}

const Dashboard: FC<OwnProps> = ({
  background,
  pageName,
  hideNavOnPrint,
  segment,
  isPublicPage,
  authUser: propsAuthUser,
  childRenderer,
}: OwnProps) => {
  const { data: auth } = useAuth()

  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [recruitmentOpen, setRecruitmentOpen] = useState(false)
  const [surveyClosed, setSurveyClosed] = useState(false)
  const [inviteModalDismissed, setInviteModalDismissed] = useState(false)
  const intervalRef = useRef<number>()
  const {
    me: stateAuthUser,
    meState,
    updateSettingsState,
  } = useAppStateSelector(state => state.user)
  const authUser = stateAuthUser || propsAuthUser
  const { fetch: loadMe } = useLoadAuthUser()
  const { data: isNewSidebarEnabled } = useSideNavFlag()
  const { post: updateUser } = updateUserSettings({ current_segment: segment })

  const { result: criteriaResult } = useUserCriteria(
    'product_survey_recruitment',
  )

  // Functions
  const checkUser = () => {
    const authToken = localAuthToken()

    if (!authToken) goToLogin()
    else if (!authUser) {
      loadMe()
    }
  }

  const triggerHubSpot = () => {
    type WindowWithHubspot = Window & { _hsq: unknown[] }
    const hsWindow = window as unknown as WindowWithHubspot

    const _hsq = (hsWindow._hsq = hsWindow._hsq || [])
    if (authUser) {
      _hsq.push([
        'identify',
        {
          email: authUser.email,
        },
      ])
    }
    _hsq.push(['setPath', hsWindow.location.pathname])
    _hsq.push(['trackPageView'])
  }

  const setProfitWell = () => {
    type WindowWithProfitWell = Window & {
      profitwell?: (a: string, b: unknown) => void
    }
    const profitWellWindow = window as unknown as WindowWithProfitWell

    const profitwell = profitWellWindow.profitwell

    if (authUser && profitwell) {
      profitwell('start', { user_id: authUser.team.customer_id })
    }
  }

  const triggerRudderStack = () => {
    if (!authUser) return

    const discType = authUser.profile.personality?.disc_type

    rsIdentify(
      authUser.id,
      {
        profile_id: authUser.id,
        email: authUser.email,
        firstName: authUser.first_name,
        lastName: authUser.last_name,
        organization_id: authUser.team.id,
        hubspot_organization_id: authUser.team.hubspot_organization_id,
        job_title: authUser.job_title,
        created_at: authUser.created_at,
        role: authUser.role,
        gender: authUser.gender,
        purchased: currentPlans(authUser).map(plan => plan.plan_id),
        access: currentAccess(authUser).join(';'),
        company: authUser.company_name,
        member_manager: isAdmin(authUser),
        archetype: discType ? toArchetype(discType) : '',
        disc_type: discType || '',
        paying: !isFreeUser(authUser),
        hiring_license_assigned: isHiringUser(authUser),
        sales_license_assigned: isSalesUser(authUser),
        teams_license_assigned: isLeadershipUser(authUser),
        in_enterprise_org: isEnterpriseUser(authUser),
      },
      { groupId: authUser.team.hubspot_organization_id },
    )
    rsGroup(authUser.team.id, {
      name: authUser.team.name,
      organization_id: authUser.team.id,
      active_products: currentPlans(authUser).map(plan => plan.type),
    })
  }

  const goToLogin = (gotUnauthed = false) => {
    ls.set('afterLoginPath', window.location.pathname + window.location.search)
    Router.push(`/app/login${gotUnauthed ? '?unauthenticated=true' : ''}`)
  }

  const currentAuthSegment = () => {
    const currSegment = authUser ? currentSegment(authUser) : null

    if (currSegment === null) return null

    return segment || currSegment
  }

  const toggleInviteModal = () => {
    setInviteModalOpen(!inviteModalOpen)
    setInviteModalDismissed(true)
  }

  const checkForTerms = () => {
    if (
      authUser &&
      !['terms', 'onboarding'].includes(pageName) &&
      customTermsOfServiceNotAccepted(authUser)
    ) {
      const currentPath = Router.asPath
      Router.push('/app/terms?redirect=' + currentPath)
    }
  }

  const getSessionStartTime = (): moment.Moment => {
    let startTime = sessionStorage.getItem(SESSION_START_TIME_KEY)

    if (!startTime) {
      startTime = String(new Date().getTime())
      sessionStorage.setItem(SESSION_START_TIME_KEY, startTime)
    }

    return moment(Number(startTime))
  }

  const getSessionLength = () => {
    const now = moment()
    const start = getSessionStartTime()
    const seconds = moment.duration(now.diff(start)).as('seconds')

    return seconds
  }

  const intervalFunc = () => {
    if (getSessionLength() >= 60 && !surveyClosed && !recruitmentOpen) {
      setRecruitmentOpen(true)
    }
  }

  const shouldOpenInviteModal = () => {
    if (inviteModalDismissed) return false

    const hasInvites = authUser?.team_invites.length
    const loginQuery =
      ls.get<ParsedUrlQuery | null>(AUTH_QUERY_PARAMS_STORAGE_KEY) || {}
    const currentTeamId = authUser?.team?.id
    const isAlreadyTeamMember =
      currentTeamId &&
      (loginQuery?.team_id === currentTeamId ||
        authUser?.team_invites[0]?.team_id === currentTeamId)
    return (
      (hasInvites || getQueryParam(loginQuery.team_id)) && !isAlreadyTeamMember
    )
  }

  const initiateIntervalIfQualified = (
    criteria: UserCriteriaResponse,
    user: FullAuthUser,
  ) => {
    if (
      criteria.created_playbook &&
      criteria.team_manager &&
      criteria.team_size > 5 &&
      !user.actions[USER_ACTIONS.closed_product_survey_recruitment]
    ) {
      intervalRef.current = window.setInterval(intervalFunc, 1000)
    }
  }

  useEffect(() => {
    if (auth?.jwtPayload && !auth.jwtPayload.impersonated_by) {
      const jwtPayload = auth.jwtPayload

      try {
        FullStoryAPI('identify', jwtPayload.sub?.split(':')[1], {
          displayName: jwtPayload.name,
          email: jwtPayload.name,
        })
      } catch (e) {}
    }
  }, [auth])

  // Effects
  useEffect(() => {
    window.scrollTo(0, 0)
    triggerHubSpot()
    checkUser()
    setProfitWell()
    triggerRudderStack()
    checkForTerms()

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  useEffect(() => {
    if (criteriaResult && authUser && !intervalRef.current) {
      initiateIntervalIfQualified(criteriaResult, authUser)
    }
  }, [criteriaResult, authUser])

  useEffect(() => {
    const currSegment = authUser ? currentSegment(authUser) : null
    const segmentChanged = segment && currSegment && currSegment !== segment
    const cantSwitch =
      authUser && segment === 'hiring' && hiringAccessRevoked(authUser)

    if (
      segment &&
      segmentChanged &&
      updateSettingsState !== 'loading' &&
      !cantSwitch
    ) {
      updateUser()
    }
  }, [segment])

  useEffect(() => {
    if (meState === 'failure') {
      deleteCookie(CRYSTAL_AUTH_TOKEN)
      deleteCookie(CRYSTAL_SESSION_TOKEN)

      if (isPublicPage) window.location.reload()
      else goToLogin(true)
    }

    if (meState === 'success') {
      triggerRudderStack()
      checkForTerms()
      if (shouldOpenInviteModal()) setInviteModalOpen(true)
    }
  }, [meState])

  useEffect(() => {
    // If the user has looked up a profile, update the authUser state.
    updateUser()
  }, [
    propsAuthUser?.chrome_extension_views.usage,
    propsAuthUser?.prediction_tally?.usage,
  ])

  const renderFullstory = () => {
    return <FullStory org="5WWQV" />
  }

  const renderUpgradeBanner = (authUser: FullAuthUser) => {
    if (!isOnFreeTeam(authUser)) return null

    return <UpgradeBanner pageName={pageName} />
  }

  if (!authUser) return <LoadingBox />
  return (
    <div
      className={classNames('wrapper')}
      style={{
        background,
        overflow: isNewSidebarEnabled ? 'visible' : 'hidden',
      }}
      id="DashboardWrapper"
    >
      {renderFullstory()}
      <Toast />
      <div className={classNames('no-print')}>
        <div className={classNames('minWidth781')}>
          {authUser && authUser.impersonation && (
            <NotificationBar
              authUser={authUser}
              pageName={pageName}
              currentSegment={currentAuthSegment()}
              hideNotifications={false}
              createUserBoolean={() => {}}
            />
          )}
        </div>
      </div>
      <div
        className={classNames('only-print', {
          tall: authUser.profile && !authUser.profile.personality,
          hideNavOnPrint,
        })}
      ></div>
      <MainContent>{childRenderer(authUser)}</MainContent>
      <OrganizationInviteManager
        authUser={authUser}
        toggleModal={toggleInviteModal}
        modalOpen={inviteModalOpen}
      />
      <ProductSurveyRecruitment
        active={recruitmentOpen}
        onClose={() => {
          setRecruitmentOpen(false)
          setSurveyClosed(true)
          intervalRef.current && clearInterval(intervalRef.current)
        }}
      />
      {renderUpgradeBanner(authUser)}
    </div>
  )
}

export default Dashboard
